import type { Session } from '@supabase/supabase-js';
import { resolveActiveTenantId } from './effectiveTenant.js';

export const AUTH_STORAGE_KEY = 'niveo.auth';
const AUTH_KEYS = [AUTH_STORAGE_KEY, `${AUTH_STORAGE_KEY}-code-verifier`, `${AUTH_STORAGE_KEY}-user`];
let generation = 0;
const memory = new Map<string, string>();

// This is the SDK's storage adapter, not a second session cache. If browser
// storage becomes unavailable, never read its older account again this runtime.
let memoryOnly = false;
function activateMemoryStorage(): void {
  memoryOnly = true;
  memory.clear();
  // Quota errors can block writes while removal still works. Remove only SDK
  // auth keys so a reload cannot recover the previous account in that case.
  try {
    const backing = localStorage;
    for (const key of AUTH_KEYS) {
      try { backing.removeItem(key); } catch { /* storage may forbid removal */ }
    }
  } catch { /* access to localStorage itself may be forbidden */ }
}
const storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: key => {
    if (!memoryOnly) {
      try { return localStorage.getItem(key); }
      catch { activateMemoryStorage(); }
    }
    return memory.get(key) ?? null;
  },
  setItem: (key, value) => {
    if (!memoryOnly) {
      try { localStorage.setItem(key, value); return; }
      catch { activateMemoryStorage(); }
    }
    memory.set(key, value);
  },
  removeItem: key => {
    if (!memoryOnly) {
      try { localStorage.removeItem(key); return; }
      catch { activateMemoryStorage(); }
    }
    memory.delete(key);
  },
};
const isUuid = (value: unknown): value is string => typeof value === 'string'
  && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
const isPositiveInteger = (value: unknown): value is number => typeof value === 'number'
  && Number.isSafeInteger(value) && value > 0;

export function createAuthStorageGeneration(fetcher: typeof fetch): {
  storage: typeof storage; fetch: typeof fetch; isCurrent: () => boolean;
} {
  const ownGeneration = generation;
  const isCurrent = () => ownGeneration === generation;
  return {
    isCurrent,
    storage: {
      getItem: key => isCurrent() ? storage.getItem(key) : null,
      setItem: (key, value) => { if (isCurrent()) storage.setItem(key, value); },
      removeItem: key => { if (isCurrent()) storage.removeItem(key); },
    },
    fetch: async (input, init) => {
      // auth-js treats every thrown fetch error as retryable. A local terminal
      // response lets the retired SDK release its auth lock without new retries.
      const retired = (): Response => new Response(JSON.stringify({
        code: 'auth_client_retired', message: 'auth_client_retired',
      }), { status: 409, headers: { 'Content-Type': 'application/json' } });
      if (!isCurrent()) return retired();
      try {
        const response = await fetcher(input, init);
        return isCurrent() ? response : retired();
      } catch (error) {
        if (!isCurrent()) return retired();
        throw error;
      }
    },
  };
}

export function retireAuthStorage(clearSession = true): void {
  generation += 1;
  if (clearSession) for (const key of AUTH_KEYS) storage.removeItem(key);
}

export function readPersistedSession(supabaseUrl: string): Session | null {
  try {
    const raw = storage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') return null;
    const session = value as Session;
    if (typeof session.access_token !== 'string' || !session.access_token
      || typeof session.refresh_token !== 'string' || !session.refresh_token
      || !isPositiveInteger(session.expires_at)
      || !isPositiveInteger(session.expires_in)
      || session.token_type?.toLowerCase() !== 'bearer'
      || !session.user || !isUuid(session.user.id)
      || session.user.aud !== 'authenticated'
      || !session.user.app_metadata || typeof session.user.app_metadata !== 'object'
      || !session.user.user_metadata || typeof session.user.user_metadata !== 'object'
      || typeof session.user.created_at !== 'string') return null;
    const parts = session.access_token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(payload.padEnd(payload.length + (4 - payload.length % 4) % 4, '='))) as Record<string, unknown>;
    if (!claims || claims.sub !== session.user.id
      || claims.iss !== `${supabaseUrl.replace(/\/$/, '')}/auth/v1`
      || !(claims.aud === 'authenticated' || (Array.isArray(claims.aud) && claims.aud.includes('authenticated')))
      || !isPositiveInteger(claims.exp)) return null;
    const tenants = claims.tenants;
    if (tenants !== undefined && (!Array.isArray(tenants) || tenants.some(id => !isUuid(id)))) return null;
    const tenantIds = (tenants ?? []) as string[];
    if (claims.tenant_id !== undefined && claims.tenant_id !== null && !isUuid(claims.tenant_id)) return null;
    if (claims.active_business_id !== undefined && claims.active_business_id !== null && !isUuid(claims.active_business_id)) return null;
    const activeTenant = resolveActiveTenantId(claims, tenantIds);
    if (activeTenant && !tenantIds.includes(activeTenant)) return null;
    // SDK expires_at can use the device clock; JWT exp uses the server clock.
    // Do not require equality or extend either expiry. Keep the original object.
    // This checks local consistency,
    // not the signature or remote revocation; AuthGate/operator policy still apply.
    return session;
  } catch {
    return null;
  }
}

/** Identity only, also used to fence a cross-tab account change during bootstrap. */
export function getPersistedAccountId(supabaseUrl: string): string | null {
  return readPersistedSession(supabaseUrl)?.user.id ?? null;
}

export function readOfflineSession(supabaseUrl: string): Session | null {
  return typeof navigator !== 'undefined' && !navigator.onLine ? readPersistedSession(supabaseUrl) : null;
}

/** A retired SDK can still broadcast; its event must agree with current storage. */
export function isCurrentPersistedSession(candidate: Session | null, supabaseUrl: string): boolean {
  const current = readPersistedSession(supabaseUrl);
  if (!candidate || !current) return candidate === null && current === null;
  return candidate.user.id === current.user.id
    && candidate.access_token === current.access_token
    && candidate.refresh_token === current.refresh_token;
}
